import axios, { AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function useRequest<T>() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<AxiosResponse | undefined>(undefined);

  const unmountedRef = useRef(false);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setData(null);
    setError(null);
    setErrorDetails(undefined);
  }, []);

  const request = useCallback((requestFunction: () => Promise<T>) => {
    setLoading(true);
    setData(null);
    setError(null);

    return (async () => {
      try {
        const response = await requestFunction();
        if (unmountedRef.current) {
          return;
        }
        setData(response);
        setLoading(false);
      } catch (e) {
        if (unmountedRef.current) {
          return;
        }
        if (axios.isAxiosError(e)) {
          setError(e.message);
        } else {
          setError((e as Error)?.message || 'An error was encountered loading data');
        }
        setErrorDetails(e.response);
        setLoading(false);
      }
    })();
  }, []);

  return { request, reset, loading, data, error, errorDetails };
}
